import { Hand, Minus, Plus } from '@timelyapp/icons/solid-icons';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import React, {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useHeldKey } from '../../../hooks/useHeldKey';
import { usePan } from '../../../hooks/usePan';
import { usePrevious } from '../../../hooks/usePrevious';
import { useZoom } from '../../../hooks/useZoom';
import { Slider } from '../../_radix-ui/slider/Slider';
import Button from '../../Button';
import { Box } from '../../box/Box';
import type { ElementProps } from '../../element/Element';
import { Elevation } from '../../elevation/Elevation';
import Tooltip from '../../Tooltip';
import { Text } from '../../text';
import styles from '../Timeline.module.scss';
import type { TimelineConfigProps, TimelineContextProps } from '../Timeline.types';

const SLIDER_WIDTH = 150;
const DEFAULT_MIN_PX_PER_MINUTE = 1;
const DEFAULT_MAX_PX_PER_MINUTE = 6;

export const TimelineContext = createContext<TimelineContextProps>({
  orientation: 'horizontal',
  fromDateTime: DateTime.now().startOf('day'),
  pxPerMinute: 2,
  minPxPerMinute: DEFAULT_MIN_PX_PER_MINUTE,
  ticksPerInterval: 4,
  panRef: null,
  scrollAreaRef: null,
  isZoomMode: false,
  isPanMode: false,
  isDragMode: false,
  isDragAddMode: false,
  isDragSubMode: false,
  isAltKeyHeld: false,
  setIsPanMode: () => {},
  setIsDragMode: () => {},
  onZoomToPx: () => null,
});
TimelineContext.displayName = 'Timeline.Context';

export const TimelineContextContainer = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { config: TimelineConfigProps }
>((props, _ref) => {
  const { className, config, ...rest } = props;
  const {
    orientation,
    ticksPerInterval,
    pxPerMinute,
    fromDateTime,
    maxPxPerMinute = DEFAULT_MAX_PX_PER_MINUTE,
    minPxPerMinute = DEFAULT_MIN_PX_PER_MINUTE,
    onZoomToPx,
  } = config;

  // Lifted from Planning Timeline, unsure of state vs useRef here, re-visit.
  const [scrollAreaRef, setScrollAreaRef] = useState<HTMLDivElement | null>(null);
  const [statefulPanMode, setIsPanMode] = useState(false);
  const [isDragMode, setIsDragMode] = useState(false);

  // Action state & modifiers
  const isAltKeyHeld = useHeldKey('Alt');
  const isSpaceHeld = useHeldKey(' ');
  const isZoomMode = useHeldKey('Meta');
  const isPanMode = (isSpaceHeld || statefulPanMode) && !isDragMode;
  const isDragAddMode = !isAltKeyHeld && !isPanMode && !isZoomMode;
  const isDragSubMode = isAltKeyHeld && !isPanMode;

  // We need the `scrollAreaRef` container to be in focus for shortcuts to work,
  // so we steal focus when user signals intent to interact with the timeline.
  const isInteracting = isAltKeyHeld || isPanMode || isZoomMode || isDragMode;
  const wasInteracting = usePrevious(isInteracting);
  useLayoutEffect(() => {
    if (scrollAreaRef && isInteracting && !wasInteracting) {
      scrollAreaRef.focus();
    }
  }, [isInteracting, wasInteracting, scrollAreaRef]);

  // Panning
  const handlePan = useCallback(
    (pan: { left: number; top: number }) => {
      scrollAreaRef!.scrollTop += pan.top;
      scrollAreaRef!.scrollLeft += pan.left;
    },
    [scrollAreaRef]
  );
  const panRef = usePan(handlePan, !isPanMode);

  // Zooming in/out by one step
  const onZoom = useCallback(
    (delta: number) => onZoomToPx?.(pxPerMinute + delta),
    [pxPerMinute, onZoomToPx]
  );
  useZoom({ onZoom, enabled: !!onZoomToPx, scrollAreaRef });

  const contextValues = useMemo(() => {
    return {
      orientation,
      pxPerMinute,
      maxPxPerMinute,
      minPxPerMinute,
      ticksPerInterval,
      panRef,
      scrollAreaRef,
      isPanMode,
      isZoomMode,
      isDragMode,
      isDragAddMode,
      isDragSubMode,
      setIsPanMode,
      setIsDragMode,
      onZoomToPx,
      fromDateTime,
      isAltKeyHeld,
    };
  }, [
    // Config
    pxPerMinute,
    maxPxPerMinute,
    minPxPerMinute,
    orientation,
    ticksPerInterval,
    // Refs
    panRef,
    scrollAreaRef,
    // State
    isPanMode,
    isZoomMode,
    isDragMode,
    isDragAddMode,
    isDragSubMode,
    fromDateTime,
    // Actions
    onZoomToPx,
    setIsPanMode,
    setIsDragMode,
    // Key modifiers
    isAltKeyHeld,
  ]);

  return (
    <TimelineContext.Provider value={contextValues}>
      <div
        ref={setScrollAreaRef}
        {...rest}
        className={classNames(
          styles.timelineContainer,
          {
            [styles.isPanMode]: isSpaceHeld || isPanMode,
            [styles.isDragAdd]: isDragAddMode,
            [styles.isDragSubtract]: isDragSubMode,
            [styles.isZoomMode]: isZoomMode,
          },
          className
        )}
      />
    </TimelineContext.Provider>
  );
});
TimelineContextContainer.displayName = 'Timeline.ContextProvider';

export const TimelineContextControls = forwardRef<HTMLDivElement, ElementProps>((props, _ref) => {
  const { children, ...rest } = props;
  const {
    isZoomMode,
    isPanMode,
    isDragAddMode,
    isDragSubMode,
    setIsPanMode,
    pxPerMinute,
    maxPxPerMinute = DEFAULT_MAX_PX_PER_MINUTE,
    minPxPerMinute = DEFAULT_MIN_PX_PER_MINUTE,
    onZoomToPx,
  } = useContext(TimelineContext);

  const sliderStepWidth = SLIDER_WIDTH / (maxPxPerMinute - 1);
  const disabledSliderWidth = (minPxPerMinute - 1) * sliderStepWidth;

  const handleZoomChange = useCallback(
    (value: number) => {
      onZoomToPx?.(Math.max(minPxPerMinute, value));
    },
    [onZoomToPx, minPxPerMinute]
  );

  const handleTogglePan = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.currentTarget?.blur();
      setIsPanMode(!isPanMode);
    },
    [isPanMode, setIsPanMode]
  );

  return (
    <Elevation
      as={Box.Row}
      gap="medium"
      padding="medium"
      content="inline"
      depth="low"
      {...rest}
      className={classNames(styles.timelineControls, props.className, {
        [styles.isActive]: isZoomMode || isPanMode || isDragAddMode || isDragSubMode,
      })}
    >
      <Tooltip
        position="top"
        fixed
        tooltip={
          isDragSubMode
            ? 'Remove time from current selections'
            : 'Make a time selection. Hold [ALT] to remove'
        }
      >
        <Button
          onClick={() => setIsPanMode(false)}
          isActive={isDragSubMode || isDragAddMode}
          priority={isDragSubMode || isDragAddMode ? 'medium' : 'low'}
          selected
          size="small"
        >
          {isDragSubMode ? <Minus /> : <Plus />}
        </Button>
      </Tooltip>
      <Tooltip position="top" fixed tooltip="Toggle click-to-pan mode">
        <Button
          onClick={handleTogglePan}
          isActive={isPanMode}
          priority={isPanMode ? 'medium' : 'low'}
          selected
          size="small"
        >
          <Hand />
        </Button>
      </Tooltip>
      <Box.Separator orientation="vertical" style={{ height: 24 }} />
      <Text.Label size="small" color="subtle">
        Zoom
      </Text.Label>
      <Tooltip position="top" tooltip="Zoom in or out [CMD+MouseWheel]">
        <Slider
          onValueChange={value => handleZoomChange(value[0])}
          defaultValue={[pxPerMinute]}
          value={[pxPerMinute]}
          min={1}
          max={maxPxPerMinute}
          step={1}
          isActive={isZoomMode}
          disabledWidth={disabledSliderWidth}
          style={{ width: SLIDER_WIDTH }}
          data-testid="timeline_zoom_slider"
        />
      </Tooltip>
      <Tooltip
        position="top"
        tooltip={
          pxPerMinute > minPxPerMinute
            ? 'Zoom out'
            : 'Minimum zoom level for current summary interval'
        }
      >
        <Button
          disabled={pxPerMinute <= minPxPerMinute}
          isActive={isZoomMode}
          size="xsmall"
          priority="low"
          onClick={() => handleZoomChange(pxPerMinute - 1)}
        >
          <Minus />
        </Button>
      </Tooltip>
      <Tooltip position="top" tooltip={pxPerMinute < maxPxPerMinute ? 'Zoom in' : 'Max zoom level'}>
        <Button
          disabled={pxPerMinute >= maxPxPerMinute}
          isActive={isZoomMode}
          size="xsmall"
          priority="low"
          onClick={() => handleZoomChange(pxPerMinute + 1)}
        >
          <Plus />
        </Button>
      </Tooltip>
      {children}
    </Elevation>
  );
});
TimelineContextControls.displayName = 'Timeline.Controls';
